import { Fragment, useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslations } from "use-intl";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useFormatOptionalDate } from "@/i18n/useFormatOptionalDate";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import { BookkeepingOption, MatchDecisionResult } from "@/types/review";
import { EdgeFunctionFailure, userFacingErrorMessage } from "@/utils/userFacingError";
import type { MatchWords } from "./useMatchLabels";

interface MatchAlternativeListProps {
  suggestionId: string;
  options: BookkeepingOption[];
  labels: MatchWords;
  /** The matcher's own words for why it couldn't decide alone. */
  explanation: string;
  formatCurrency: (amount: number, currency: string) => string;
  onDecided: (result: MatchDecisionResult) => void;
}

type OptionLink = NonNullable<BookkeepingOption["link"]>;
type AlternativesT = ReturnType<typeof useTranslations<"consultantReview.match.alternatives">>;

const verificationOf = (link: OptionLink) => `${link.series}${link.number}`;

/** Links first (they post nothing), then the bookings: the order of screens.md S3, Draft A. */
const ranked = (options: BookkeepingOption[]) => [
  ...options.filter((o) => o.kind === "link"),
  ...options.filter((o) => o.kind === "journal"),
];

function blockedText(link: OptionLink, formatCurrency: MatchAlternativeListProps["formatCurrency"], t: AlternativesT): string | null {
  if (link.approvable) return null;
  // The difference is signed (short or over); "off by" names its size.
  if (link.blockedBy === "unbalanced") return t("offBy", { amount: formatCurrency(Math.abs(link.differenceOre) / 100, "SEK") });
  if (link.blockedBy === "group") return t("group");
  return t("notApprovable");
}

function approveLabel(option: BookkeepingOption | undefined, t: AlternativesT): string {
  if (!option) return t("choose");
  return option.kind === "link" && option.link ? t("approveLink", { verification: verificationOf(option.link) }) : t("bookIt");
}

function AlternativeRow({ option, labels, formatCurrency }: {
  option: BookkeepingOption;
  labels: MatchWords;
  formatCurrency: MatchAlternativeListProps["formatCurrency"];
}) {
  const t = useTranslations("consultantReview.match.alternatives");
  const formatOptionalDate = useFormatOptionalDate();
  const link = option.kind === "link" ? option.link : undefined;
  const blocked = link ? blockedText(link, formatCurrency, t) : null;
  const id = `match-alternative-${option.id}`;
  return (
    <div className="flex items-start gap-3 rounded-lg border p-4" data-testid="match-alternative">
      <RadioGroupItem value={option.id} id={id} disabled={blocked !== null} className="mt-1" />
      <Label htmlFor={id} className="flex-1 space-y-1 font-normal">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium">
            {link
              ? t("rowTitle", { target: labels.targetName, label: verificationOf(link), date: formatOptionalDate(link.date) })
              : option.label}
          </span>
          <Badge variant="outline">
            {link ? t("linkBadge") : t("bookBadge", { count: option.accountingEntries?.length ?? 0 })}
          </Badge>
          {blocked && <Badge variant="secondary">{blocked}</Badge>}
        </span>
        {link && <span className="block text-sm">{formatCurrency(link.amountOre / 100, "SEK")}</span>}
        {!link && option.accountingEntries?.map((entry) => (
          <span key={entry} className="block font-mono text-sm text-muted-foreground">{entry}</span>
        ))}
      </Label>
    </div>
  );
}

/**
 * FIN-1071 S3: a matcher couldn't decide alone. The person links the item to a booking already in
 * the books, or books it, through the one decision endpoint every matcher shares.
 */
export function MatchAlternativeList({
  suggestionId, options, labels, explanation, formatCurrency, onDecided,
}: MatchAlternativeListProps) {
  const t = useTranslations("consultantReview.match.alternatives");
  const ordered = ranked(options);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolvedElsewhere, setResolvedElsewhere] = useState(false);
  const selected = ordered.find((o) => o.id === selectedId);

  const approve = async () => {
    if (!selected || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      onDecided(await ServiceRegistry.getMatchReviewService().approve(suggestionId, selected.id));
    } catch (err) {
      // A 409 is a colleague's decision or booking: this item is no longer ours to decide.
      setResolvedElsewhere(err instanceof EdgeFunctionFailure && err.status === 409);
      setError(userFacingErrorMessage(err, t("saveFailed")));
    } finally {
      setIsSubmitting(false);
    }
  };

  const firstJournal = ordered.findIndex((o) => o.kind === "journal");
  return (
    <div className="space-y-4" data-testid="match-alternatives">
      <div>
        <h3 className="mb-1 text-lg font-semibold">{t("title", { item: labels.item })}</h3>
        <p className="text-sm text-muted-foreground">{explanation}</p>
      </div>

      <RadioGroup
        value={selectedId ?? ""}
        onValueChange={setSelectedId}
        disabled={isSubmitting || resolvedElsewhere}
        aria-label={t("ariaLabel")}
        className="gap-3"
      >
        {ordered.map((option, index) => (
          <Fragment key={option.id}>
            {index === firstJournal && index > 0 && (
              <p className="text-center text-sm text-muted-foreground">{t("or")}</p>
            )}
            <AlternativeRow option={option} labels={labels} formatCurrency={formatCurrency} />
          </Fragment>
        ))}
      </RadioGroup>

      {error && (
        <Alert variant={resolvedElsewhere ? "default" : "destructive"} role="alert">
          <AlertTitle>{resolvedElsewhere ? t("decidedTitle") : t("notSavedTitle")}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="border-t pt-4">
        <Button className="w-full" onClick={approve} disabled={!selected || isSubmitting || resolvedElsewhere}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {approveLabel(selected, t)}
        </Button>
      </div>
    </div>
  );
}
